import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneOutgoing, Loader2, CheckCircle2, AlertCircle, Phone, Sparkles, XCircle, RotateCcw, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@component/dialog";
import { Button } from "@component/button";
import { Label } from "@component/label";
import { Input } from "@component/input";
import { initiateTestCall, getAgentCallSession } from "@service/agent";
import type { SessionStatus } from "@type/agent";

interface TestCallDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    campaignId: string;
    onCallInitiated?: (sessionId: string) => void;
}

const POLL_STATUSES: SessionStatus[] = ["pending", "scheduled", "active", "analyzing"];
const POLL_INTERVAL = 5000;

const statusConfig: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; color: string; bg: string; animate?: boolean }> = {
    pending: { label: "Waiting to connect...", icon: Phone, color: "text-gray-600", bg: "bg-gray-50 border border-gray-200", animate: true },
    scheduled: { label: "Call scheduled...", icon: Phone, color: "text-indigo-600", bg: "bg-indigo-50 border border-indigo-200", animate: true },
    active: { label: "Call in progress...", icon: Phone, color: "text-blue-600", bg: "bg-blue-50 border border-blue-200", animate: true },
    analyzing: { label: "Call ended. Analyzing results...", icon: Sparkles, color: "text-amber-600", bg: "bg-amber-50 border border-amber-200", animate: true },
    results_ready: { label: "Call complete. Results ready!", icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50 border border-green-200" },
    failed: { label: "Call failed", icon: XCircle, color: "text-red-600", bg: "bg-red-50 border border-red-200" },
};

type Phase = "input" | "tracking" | "complete";

export function TestCallDialog({ open, onOpenChange, campaignId, onCallInitiated }: TestCallDialogProps) {
    const navigate = useNavigate();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [name, setName] = useState("");
    const [merchant, setMerchant] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [phase, setPhase] = useState<Phase>("input");
    const [trackingSessionId, setTrackingSessionId] = useState<string | null>(null);
    const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(null);
    const [callScore, setCallScore] = useState<number | null>(null);
    const [callDuration, setCallDuration] = useState<number | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clearPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    // Poll for session status updates
    useEffect(() => {
        if (!trackingSessionId || !sessionStatus) return;
        if (!POLL_STATUSES.includes(sessionStatus)) {
            clearPolling();
            return;
        }

        pollRef.current = setInterval(async () => {
            try {
                const updated = await getAgentCallSession(trackingSessionId, true);
                if (updated) {
                    setSessionStatus(updated.status);
                    if (!POLL_STATUSES.includes(updated.status)) {
                        clearPolling();
                        setPhase("complete");
                        if (updated.score != null) setCallScore(updated.score);
                        if (updated.duration != null) setCallDuration(updated.duration);
                        if (onCallInitiated) onCallInitiated(trackingSessionId);
                    }
                }
            } catch {
                // Silent - polling failure is non-critical
            }
        }, POLL_INTERVAL);

        return clearPolling;
    }, [trackingSessionId, sessionStatus]);

    // Clean up on unmount
    useEffect(() => clearPolling, []);

    const handleSend = async () => {
        if (!phoneNumber.trim()) return;
        setSending(true);
        setError(null);
        try {
            const res = await initiateTestCall(
                campaignId,
                phoneNumber.trim(),
                name.trim() || undefined,
                merchant.trim() || undefined,
            );
            const sessionId = res.sessions[0]?.sessionId;
            if (!sessionId) {
                setError("Call initiated but no session ID returned. Please check the call log.");
                setSending(false);
                return;
            }
            setTrackingSessionId(sessionId);
            setSessionStatus("pending");
            setPhase("tracking");
        } catch (err: unknown) {
            const e = err as { response?: { status?: number; data?: { message?: string } } };
            const status = e?.response?.status;
            const serverMessage = e?.response?.data?.message;

            let message = "Failed to initiate test call.";
            if (status === 400) {
                message = serverMessage || "Campaign has no voice agent or phone number configured.";
            } else if (status === 404) {
                message = serverMessage || "Campaign not found.";
            } else if (status === 500) {
                message = serverMessage || "Voice provider call failed.";
            }

            setError(message);
        } finally {
            setSending(false);
        }
    };

    const handleReset = () => {
        clearPolling();
        setPhase("input");
        setTrackingSessionId(null);
        setSessionStatus(null);
        setCallScore(null);
        setCallDuration(null);
        setError(null);
        setPhoneNumber("");
        setName("");
        setMerchant("");
    };

    const handleClose = () => {
        clearPolling();
        onOpenChange(false);
        setTimeout(() => {
            setPhase("input");
            setTrackingSessionId(null);
            setSessionStatus(null);
            setCallScore(null);
            setCallDuration(null);
            setError(null);
            setPhoneNumber("");
            setName("");
            setMerchant("");
        }, 200);
    };

    const canSend = phoneNumber.trim().length > 0 && !sending;
    const currentStatus = sessionStatus ? statusConfig[sessionStatus] : null;

    const formatDuration = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PhoneOutgoing className="w-5 h-5 text-blue-600" />
                        Test Campaign
                    </DialogTitle>
                </DialogHeader>

                {phase === "input" && (
                    <>
                        <p className="text-sm text-gray-500 -mt-2">
                            Place a test campaign call.
                        </p>

                        <div className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label htmlFor="test-phone">Phone Number *</Label>
                                <Input
                                    id="test-phone"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    placeholder="+60 12 345 6789"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="test-name">Name</Label>
                                <Input
                                    id="test-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Contact name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="test-merchant">Merchant</Label>
                                <Input
                                    id="test-merchant"
                                    value={merchant}
                                    onChange={(e) => setMerchant(e.target.value)}
                                    placeholder="Merchant name"
                                />
                            </div>

                            {error && (
                                <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 mt-4">
                            <Button variant="ghost" onClick={handleClose}>
                                Close
                            </Button>
                            <Button onClick={handleSend} disabled={!canSend}>
                                {sending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                        Calling...
                                    </>
                                ) : (
                                    <>
                                        <PhoneOutgoing className="w-4 h-4 mr-1.5" />
                                        Send Test Campaign
                                    </>
                                )}
                            </Button>
                        </div>
                    </>
                )}

                {phase === "tracking" && currentStatus && (
                    <>
                        <div className="flex flex-col items-center gap-4 py-6">
                            <div className={`p-4 rounded-full ${currentStatus.bg}`}>
                                <currentStatus.icon className={`w-8 h-8 ${currentStatus.color} ${currentStatus.animate ? "animate-pulse" : ""}`} />
                            </div>
                            <div className="text-center space-y-1">
                                <p className={`text-sm font-medium ${currentStatus.color}`}>
                                    {currentStatus.label}
                                </p>
                                <p className="text-xs text-gray-400">
                                    Calling {phoneNumber}{name ? ` (${name})` : ""}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 mt-2">
                            <Button variant="ghost" onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    </>
                )}

                {phase === "complete" && currentStatus && (
                    <>
                        <div className="flex flex-col items-center gap-4 py-6">
                            <div className={`p-4 rounded-full ${currentStatus.bg}`}>
                                <currentStatus.icon className={`w-8 h-8 ${currentStatus.color}`} />
                            </div>
                            <div className="text-center space-y-1">
                                <p className={`text-sm font-medium ${currentStatus.color}`}>
                                    {currentStatus.label}
                                </p>
                                {(callDuration != null || callScore != null) && (
                                    <div className="flex items-center justify-center gap-3 text-xs text-gray-500 mt-1">
                                        {callDuration != null && <span>Duration: {formatDuration(callDuration)}</span>}
                                        {callScore != null && <span>Score: {callScore}</span>}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/agent/calls/${trackingSessionId}`)}
                            >
                                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                                View Details
                            </Button>
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" onClick={handleReset}>
                                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                    Test Again
                                </Button>
                                <Button onClick={handleClose}>
                                    Done
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
